import { NavLink } from 'react-router-dom'
import {
  Layers,
  Crosshair,
  Heart,
  UserCircle,
  FileSignature,
  DollarSign,
  Home,
  BarChart3,
  Target,
  Calendar,
  Film,
  Newspaper,
  BookOpen,
  MessageSquarePlus,
  Users,
  Package,
  CreditCard,
  Database,
  ListTodo,
  ClipboardList,
  Mail,
  FileText,
  Workflow,
  UserCog,
  ShieldCheck,
  Key,
  Send,
  Upload,
  Search,
  Layers3,
  Ban,
} from 'lucide-react'
import { cn } from '@/lib/cn'
import { useTheme } from '@/contexts/ThemeContext'

// Structure ported from Emergent production AppLayout.js.
// Plus a "Prospecting" section for the team_crm-specific features
// that aren't in the original Emergent CRM.

type NavItem = {
  to: string
  label: string
  icon?: typeof Users
  end?: boolean
}

const crmItems: NavItem[] = [
  { to: '/boards', label: 'Member Tracker', icon: Layers },
  { to: '/leads', label: 'Lead Finder', icon: Crosshair },
  { to: '/nurturing', label: 'Companies', icon: Heart },
  { to: '/contacts', label: 'Contacts', icon: UserCircle },
  { to: '/proposals', label: 'Proposals', icon: FileSignature },
  { to: '/contracts', label: 'Contracts & Payments', icon: DollarSign },
]

const taskItems: NavItem[] = [
  { to: '/tasks?filter=my', label: 'My Tasks' },
  { to: '/tasks?filter=today', label: "Today's Tasks" },
  { to: '/tasks?filter=overdue', label: 'Overdue Tasks' },
  { to: '/tasks', label: 'All Tasks', end: true },
]

const analyticsItems: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: Home, end: true },
  { to: '/intelligence', label: 'Intelligence', icon: BarChart3 },
  { to: '/goals', label: 'Goals', icon: Target },
  { to: '/analytics', label: 'Prospecting Analytics', icon: BarChart3 },
]

const operationsItems: NavItem[] = [
  { to: '/events', label: 'Events Calendar', icon: Calendar },
  { to: '/media-boards', label: 'Media Production', icon: Film },
  { to: '/press-releases', label: 'Press Releases', icon: Newspaper },
]

const prospectingItems: NavItem[] = [
  { to: '/icps', label: 'ICPs', icon: Target },
  { to: '/imports', label: 'Lead Imports', icon: Upload },
  { to: '/scrape-jobs', label: 'Scrape Jobs', icon: Search },
  { to: '/sequences', label: 'Sequences', icon: Layers3 },
  { to: '/opt-outs', label: 'Opt-outs', icon: Ban },
  { to: '/extension-tokens', label: 'Extension Tokens', icon: Key },
]

const supportItems: NavItem[] = [
  { to: '/docs', label: 'Documentation', icon: BookOpen },
  { to: '/support', label: 'Bugs & Requests', icon: MessageSquarePlus },
]

const adminItems: NavItem[] = [
  { to: '/users', label: 'Users', icon: Users },
  { to: '/products', label: 'Products', icon: Package },
  { to: '/proposal-templates', label: 'Proposal Templates', icon: FileSignature },
  { to: '/templates', label: 'Section Templates', icon: BookOpen },
  { to: '/settings/billing', label: 'Payment Settings', icon: CreditCard },
  { to: '/invoices', label: 'Invoices', icon: FileText },
  { to: '/payments', label: 'Payments', icon: DollarSign },
  { to: '/crm-imports', label: 'Data Import', icon: Database },
  { to: '/backlog', label: 'Product Backlog', icon: ListTodo },
  { to: '/contact-submissions', label: 'Contact Submissions', icon: ClipboardList },
  { to: '/system-emails', label: 'System Emails', icon: Mail },
  { to: '/synced-emails', label: 'Synced Emails', icon: Mail },
  { to: '/bulk-send', label: 'Bulk Send', icon: Send },
  { to: '/settings/brevo', label: 'Brevo Integration', icon: Mail },
  { to: '/settings/gmail', label: 'Gmail Settings', icon: Mail },
  { to: '/settings/upload-portals', label: 'Upload Portals', icon: Upload },
  { to: '/admin-pin', label: 'Admin PIN', icon: ShieldCheck },
  { to: '/settings', label: 'Prospecting Settings', icon: Workflow },
  { to: '/settings/user', label: 'My Settings', icon: UserCog },
]

type NavGroup = { title: string; items: NavItem[] }

const groups: NavGroup[] = [
  { title: 'CRM', items: crmItems },
  { title: 'Tasks', items: taskItems },
  { title: 'Analytics', items: analyticsItems },
  { title: 'Operations', items: operationsItems },
  { title: 'Prospecting', items: prospectingItems },
  { title: 'Support', items: supportItems },
  { title: 'Admin', items: adminItems },
]

function NavRow({ to, label, icon: Icon, end, isDark }: NavItem & { isDark: boolean }) {
  return (
    <li>
      <NavLink
        to={to}
        end={end}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition-colors',
            isActive
              ? isDark
                ? 'bg-[#A2FF00]/10 text-[#A2FF00] font-medium'
                : 'bg-primary/10 text-primary font-medium'
              : isDark
                ? 'text-gray-400 hover:bg-white/5 hover:text-white'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
          )
        }
      >
        {Icon ? <Icon className="h-4 w-4 shrink-0" /> : <span className="w-4" />}
        {label}
      </NavLink>
    </li>
  )
}

export function Sidebar() {
  const { isDark } = useTheme()

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-40 flex w-60 flex-col border-r transition-colors',
        isDark
          ? 'bg-[#0A0A0A]/80 backdrop-blur-sm border-white/10'
          : 'bg-card border-border',
      )}
    >
      {/* Logo — Unbounded font + QSECDEF green */}
      <div
        className={cn(
          'flex h-14 items-center gap-2.5 border-b px-4',
          isDark ? 'border-white/10' : 'border-border',
        )}
      >
        <span
          className="text-xl font-bold text-[#A2FF00]"
          style={{ fontFamily: 'Unbounded, sans-serif' }}
        >
          team_crm
        </span>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3">
        {groups.map((group) => (
          <div key={group.title} className="mt-1 px-2 first:mt-0">
            <p
              className={cn(
                'mb-1 px-3 pt-2 text-xs font-semibold uppercase tracking-wide',
                isDark ? 'text-gray-500' : 'text-muted-foreground/60',
              )}
            >
              {group.title}
            </p>
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <NavRow key={item.to} {...item} isDark={isDark} />
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div
        className={cn(
          'border-t p-3',
          isDark ? 'border-white/10' : 'border-border',
        )}
      >
        <p className={cn('text-xs px-2', isDark ? 'text-gray-500' : 'text-muted-foreground')}>
          team_crm v1.0
        </p>
      </div>
    </aside>
  )
}
